/**
 * Kesho design behaviours: scroll reveals, hero parallax, the header's scroll
 * shadow, and the menu / search overlays.
 *
 * Cart, variant picking and predictive search stay with Horizon — this file
 * never touches them beyond keeping the header's cart count in step.
 */

import { StandardEvents } from '@shopify/events';

const REDUCED = matchMedia('(prefers-reduced-motion: reduce)');

/* ------------------------------------------------------------- reveals -- */

/**
 * One shared observer for the whole page. Elements unobserve themselves after
 * firing, and a failsafe reveals everything after 2.6s so slow or missed
 * intersections can never strand content invisible.
 */
const revealObserver =
  'IntersectionObserver' in window
    ? new IntersectionObserver(
        (entries) => {
          let staggered = 0;

          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            // Cap the stagger at 5 items so a long grid does not crawl in
            entry.target.style.setProperty('--kesho-reveal-delay', `${Math.min(staggered, 4) * 55}ms`);
            entry.target.setAttribute('data-revealed', '');
            revealObserver.unobserve(entry.target);
            staggered += 1;
          }
        },
        { threshold: 0.08, rootMargin: '0px 0px -6% 0px' }
      )
    : null;

function revealAll() {
  for (const el of document.querySelectorAll('.kesho-reveal')) el.setAttribute('data-revealed', '');
}

function scanReveals() {
  if (!revealObserver || REDUCED.matches) return revealAll();
  for (const el of document.querySelectorAll('.kesho-reveal:not([data-revealed])')) revealObserver.observe(el);
}

/* ------------------------------------------- parallax + header shadow -- */

const header = document.querySelector('[data-kesho-header]');
const parallax = document.querySelector('[data-kesho-parallax]');
let ticking = false;

/**
 * Above 990px base.css makes `.page-wrapper` the scroller and freezes the
 * document, so `window.scrollY` never moves. Read the offset from whichever
 * element actually scrolled.
 */
function offsetOf(target) {
  if (!target || target === document || target === window) return window.scrollY || 0;
  return target.scrollTop || 0;
}

function onScroll(target) {
  if (ticking) return;
  ticking = true;

  requestAnimationFrame(() => {
    const y = offsetOf(target);
    if (parallax && !REDUCED.matches) parallax.style.transform = `translateY(${(y * 0.18).toFixed(1)}px)`;
    if (header) header.toggleAttribute('data-scrolled', y > 24);
    ticking = false;
  });
}

/* ------------------------------------------------------------ overlays -- */

const scrim = document.querySelector('[data-kesho-scrim]');

/** Only one overlay is ever open; opening any closes the others. */
function closeOverlays() {
  for (const el of document.querySelectorAll('[data-kesho-overlay]')) {
    el.removeAttribute('data-open');
    el.setAttribute('inert', '');
  }
  for (const el of document.querySelectorAll('[data-kesho-open]')) el.setAttribute('aria-expanded', 'false');
  scrim?.removeAttribute('data-open');
  document.documentElement.removeAttribute('scroll-lock');
}

function openOverlay(name) {
  closeOverlays();

  const panel = document.querySelector(`[data-kesho-overlay="${name}"]`);
  if (!panel) return;

  panel.removeAttribute('inert');
  panel.setAttribute('data-open', '');
  scrim?.setAttribute('data-open', '');
  document.documentElement.setAttribute('scroll-lock', '');

  for (const trigger of document.querySelectorAll(`[data-kesho-open="${name}"]`)) {
    trigger.setAttribute('aria-expanded', 'true');
  }

  // The design autofocuses the search field once the panel has finished dropping
  if (name === 'search') {
    setTimeout(() => panel.querySelector('[data-kesho-search-input]')?.focus(), 350);
  }
}

document.addEventListener('click', (event) => {
  const opener = event.target.closest?.('[data-kesho-open]');
  if (opener) {
    event.preventDefault();
    openOverlay(opener.dataset.keshoOpen);
    return;
  }

  if (event.target.closest?.('[data-kesho-close], [data-kesho-scrim]')) {
    event.preventDefault();
    closeOverlays();
    return;
  }

  // Footer "Back to top". Above 990px `.page-wrapper` is the scroller, not the
  // window (see offsetOf), so a plain "#" link would not move the page.
  if (event.target.closest?.('[data-kesho-to-top]')) {
    event.preventDefault();
    const behavior = REDUCED.matches ? 'auto' : 'smooth';
    document.querySelector('.page-wrapper')?.scrollTo({ top: 0, behavior });
    window.scrollTo({ top: 0, behavior });
  }
});

document.addEventListener('keydown', (event) => {
  if (event.key === 'Escape') closeOverlays();
});

/* ------------------------------------------------------- variant picker -- */

/**
 * Drives the wash and size pills on the product page.
 *
 * Horizon ships its own `variant-picker`, but its markup and settings contract
 * are tied to the theme-block layout; the design's swatch and size pills are
 * simpler to own outright than to override. Selection still writes to the
 * hidden `id` input that Horizon's product form reads, so add-to-cart, the
 * drawer and the cart all behave exactly as they do elsewhere.
 */
function initVariantPicker(root = document) {
  const data = root.querySelector('[data-kesho-variants]');
  const form = root.querySelector('[data-kesho-variant-id]')?.form;
  if (!data || !form) return;

  /** @type {Array<{id:number, options:string[], price:number, available:boolean}>} */
  let variants;
  try {
    variants = JSON.parse(data.textContent);
  } catch {
    return; // Leave the server-rendered selection in place
  }

  const idInput = form.querySelector('[data-kesho-variant-id]');
  const priceEl = root.querySelector('[data-kesho-price]');
  const button = root.querySelector('[data-kesho-atc]');
  const label = root.querySelector('[data-kesho-atc-label]');
  const addLabel = label?.textContent.split('—')[0].trim() ?? 'Add to cart';

  const money = (cents) =>
    new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency',
      currency: data.dataset.currency || Shopify?.currency?.active || 'EGP',
    }).format(cents / 100);

  function selection() {
    const picked = [];
    for (const fieldset of form.querySelectorAll('[data-kesho-option-index]')) {
      const checked = fieldset.querySelector('input:checked');
      picked[Number(fieldset.dataset.keshoOptionIndex)] = checked?.value ?? null;
    }
    return picked;
  }

  function apply() {
    const picked = selection();
    const match = variants.find((variant) =>
      picked.every((value, index) => value == null || variant.options[index] === value)
    );

    // Reflect the pill state and each fieldset's "Wash — X" readout
    for (const fieldset of form.querySelectorAll('[data-kesho-option-index]')) {
      const checked = fieldset.querySelector('input:checked');
      const readout = fieldset.querySelector('[data-kesho-option-value]');
      if (readout && checked) readout.textContent = checked.value;

      for (const input of fieldset.querySelectorAll('input')) {
        input.closest('label')?.classList.toggle('kesho-swatch--on', input.checked);
        input.closest('label')?.classList.toggle('kesho-size--on', input.checked);
      }
    }

    if (!match) return;

    if (idInput) idInput.value = String(match.id);
    if (priceEl) priceEl.textContent = money(match.price);

    if (button) button.disabled = !match.available;
    if (label) {
      label.textContent = match.available ? `${addLabel} — ${money(match.price)}` : button?.dataset.soldOut || 'Sold out';
    }

    // Keep the address bar in step, so a shared link lands on the same variant
    const url = new URL(window.location.href);
    url.searchParams.set('variant', String(match.id));
    window.history.replaceState({}, '', url);
  }

  form.addEventListener('change', (event) => {
    if (event.target.matches('input[type="radio"]')) apply();
  });

  apply();
}

/* ------------------------------------------------------ archive filters -- */

/**
 * Filters the archive in the browser. Every piece is already in the DOM and
 * sold pieces are only ever hidden by an explicit filter — never dropped.
 */
function initArchiveFilters(root = document) {
  // One grid per kind group, so this is a list, not a single node.
  const grids = root.querySelectorAll('[data-kesho-archive-grid]');
  const chips = root.querySelectorAll('[data-kesho-filter]');
  if (!grids.length || !chips.length) return;

  function applyFilter(state) {
    for (const card of root.querySelectorAll('[data-kesho-archive-grid] [data-kesho-state]')) {
      card.hidden = state !== 'all' && card.dataset.keshoState !== state;
    }
    for (const chip of chips) {
      const on = chip.dataset.keshoFilter === state;
      chip.classList.toggle('kesho-chip--active', on);
      chip.setAttribute('aria-pressed', String(on));
    }
  }

  for (const chip of chips) {
    chip.addEventListener('click', () => applyFilter(chip.dataset.keshoFilter));
  }

  // A sold piece's CTA links back here with ?filter=available
  const requested = new URLSearchParams(window.location.search).get('filter');
  if (requested && ['all', 'available', 'sold'].includes(requested)) applyFilter(requested);
}

/* ---------------------------------------------------------- cart count -- */

/* ------------------------------------------------- add to cart confirm -- */

/**
 * The button that was pressed, remembered so the confirmation lands on it rather
 * than on every add-to-cart on the page. Quick-add grids have one per card.
 */
let lastAddButton = null;

/*
 * The press and the line landing in the cart are a network round trip apart.
 * Without this the button sits inert for that whole window and the only way to
 * tell whether the press registered is to press again.
 */
const ADDING_CEILING_MS = 8000;
/* A local add can come back in under 100ms, which is a spinner nobody sees.
   Hold the waiting state this long before the confirmation is allowed to land. */
const ADDING_FLOOR_MS = 450;

function stopAdding(button) {
  if (!button) return;
  clearTimeout(button.keshoAddingTimer);
  clearTimeout(button.keshoFloorTimer);
  button.removeAttribute('data-kesho-adding');
}

document.addEventListener(
  'click',
  (event) => {
    const button = event.target.closest('button[name="add"]');
    if (!button || button.disabled) return;
    /*
     * Quick add renders two buttons that both answer to name="add": the one
     * that submits the product form, and one that only opens the variant
     * chooser. The chooser sends no cart request, so a waiting state on it
     * would spin until the ceiling cleared it. Only a button that owns a form
     * is really adding anything.
     */
    if (!button.form) return;
    lastAddButton = button;

    clearTimeout(button.keshoAddingTimer);
    button.keshoAddingAt = Date.now();
    button.setAttribute('data-kesho-adding', '');
    // An add that fails — sold out, a network error — fires no cart event, so
    // nothing else would ever take the spinner down.
    button.keshoAddingTimer = setTimeout(() => stopAdding(button), ADDING_CEILING_MS);
  },
  { capture: true }
);

const CONFIRM_MS = 1800;

/** Confirms on the button, then puts it back the way it was. */
function confirmAdded(button) {
  if (!button) return;

  // Let the waiting state serve its floor before the confirmation replaces it.
  const waited = Date.now() - (button.keshoAddingAt ?? 0);
  if (button.hasAttribute('data-kesho-adding') && waited < ADDING_FLOOR_MS) {
    clearTimeout(button.keshoFloorTimer);
    button.keshoFloorTimer = setTimeout(() => confirmAdded(button), ADDING_FLOOR_MS - waited);
    return;
  }

  stopAdding(button);
  clearTimeout(button.keshoConfirmTimer);
  button.setAttribute('data-kesho-added', '');
  button.keshoConfirmTimer = setTimeout(() => button.removeAttribute('data-kesho-added'), CONFIRM_MS);
}

function bumpCartCount() {
  for (const el of document.querySelectorAll('[data-kesho-cart-count]')) {
    // Removed and re-set on the next frame, or the animation will not replay on
    // a second add while the first is still on screen.
    el.removeAttribute('data-kesho-bumped');
    requestAnimationFrame(() => el.setAttribute('data-kesho-bumped', ''));
  }
}

/*
 * Hooked to the cart event rather than to the click, so the confirmation only
 * appears once the line is actually in the cart — and so it covers every
 * add-to-cart in the theme, including quick add, without touching each one.
 */
document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  event.promise?.then(({ cart, detail }) => {
    const count = cart?.totalQuantity ?? detail?.itemCount;
    if (count == null) return;
    for (const el of document.querySelectorAll('[data-kesho-cart-count]')) el.textContent = String(count);
    confirmAdded(lastAddButton);
    bumpCartCount();
  });
});

/* ---------------------------------------------------- product gallery -- */

/**
 * Switches which photograph the large frame is showing. Every frame is already
 * in the DOM, so this only toggles `hidden` — no request, no decode, no flash of
 * an empty stage between images.
 *
 * Arrow keys move along the rail, because a keyboard user should not have to tab
 * through every thumbnail to reach the last one.
 */
function initProductGallery(root = document) {
  for (const gallery of root.querySelectorAll('[data-kesho-gallery]')) {
    const frames = gallery.querySelectorAll('[data-kesho-frame]');
    const thumbs = gallery.querySelectorAll('[data-kesho-thumb]');
    if (frames.length < 2 || !thumbs.length) continue;

    function show(index) {
      for (const frame of frames) frame.hidden = frame.dataset.keshoFrame !== String(index);
      for (const thumb of thumbs) {
        const on = thumb.dataset.keshoThumb === String(index);
        // Removed rather than set to "false": aria-current has no false state,
        // and the stylesheet keys the selected thumbnail off its presence.
        if (on) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      }
    }

    thumbs.forEach((thumb, i) => {
      thumb.addEventListener('click', () => show(i));
      thumb.addEventListener('keydown', (event) => {
        const step = event.key === 'ArrowRight' || event.key === 'ArrowDown' ? 1 : event.key === 'ArrowLeft' || event.key === 'ArrowUp' ? -1 : 0;
        if (!step) return;
        event.preventDefault();
        const next = (i + step + thumbs.length) % thumbs.length;
        thumbs[next].focus();
        show(next);
      });
    });
  }
}

/* ------------------------------------------------------------ after load -- */

/**
 * Runs `fn` once the page has loaded, or 2s after being called, whichever comes
 * first, and then when the browser is idle. Background
 * downloads (spin videos, the rest of a gallery) wait for this so they never
 * compete with the photographs the shopper is looking at on a slow connection —
 * a speed test showed them delaying the main image when they started early.
 */
function afterLoad(fn) {
  let ran = false;
  const run = () => {
    if (ran) return;
    ran = true;
    'requestIdleCallback' in window ? requestIdleCallback(fn, { timeout: 1000 }) : setTimeout(fn, 200);
  };
  if (document.readyState === 'complete') return run();
  window.addEventListener('load', run, { once: true });
  // On slow mobile data `load` can be 10s+ away, and a shopper taps long before
  // that — a phone test showed the spin still unbuffered at the tap. So 2s after
  // this runs (the page is already interactive by then) is the latest it waits.
  setTimeout(run, 2000);
}

/* --------------------------------------------------------- spin preview -- */

/**
 * Plays a product's spin over its still while the pointer or focus is on the
 * card, and reduced motion opts out before anything is bound.
 *
 * Touch screens have no hover, so there the card carries a small 360° button
 * (see snippets/kesho-media) that starts and stops the spin on tap. The spin
 * never starts on its own while scrolling — that read as distracting.
 *
 * The video used to wait for the tap or hover to start downloading, so the
 * shopper watched a still for a second or two after asking for the spin. It
 * now buffers as the card nears the screen, once the page has finished loading
 * (a few hundred KB, skipped under Data Saver), and the frame only switches to
 * the video once it is actually
 * playing — until then the button shows it is loading.
 */
let spinPreloader;

/*
 * One spin downloads at a time. Four at once filled a slow connection, so the
 * one the shopper tapped finished last (a phone test measured 9s). The queue
 * takes them in the order they come on screen; each waits until it can play
 * through, or 8s, before the next starts.
 */
const spinQueue = [];
let spinLoading = false;

function queueSpinPreload(video) {
  if (video.preload === 'auto' || video.readyState > 0 || !video.paused) return;
  spinQueue.push(video);
  pumpSpinQueue();
}

function pumpSpinQueue() {
  if (spinLoading) return;
  const video = spinQueue.shift();
  if (!video) return;
  // Already asked for (a tap got there first): load() would abort it.
  if (video.preload === 'auto' || video.readyState > 0 || !video.paused) return pumpSpinQueue();
  spinLoading = true;
  let settled = false;
  const next = () => {
    if (settled) return;
    settled = true;
    spinLoading = false;
    pumpSpinQueue();
  };
  video.addEventListener('canplaythrough', next, { once: true });
  video.addEventListener('error', next, { once: true });
  setTimeout(next, 8000);
  video.preload = 'auto';
  video.load();
}

function initSpinPreviews(root = document) {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const canHover = window.matchMedia('(hover: hover)').matches;
  const saveData = navigator.connection?.saveData === true;

  if (!saveData && 'IntersectionObserver' in window) {
    spinPreloader ??= new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          spinPreloader.unobserve(entry.target);
          const video = entry.target.querySelector('.kesho-media__spin');
          if (video) queueSpinPreload(video);
        }
      },
      { rootMargin: '200px 0px' }
    );
  }

  for (const video of root.querySelectorAll('.kesho-media__spin')) {
    const frame = video.closest('.kesho-media');
    if (!frame || frame.dataset.keshoSpinBound) continue;
    frame.dataset.keshoSpinBound = 'true';

    // An always-on spin plays itself; binding hover to it would stop it. It is
    // shown only while it really plays: iOS refuses autoplay in Low Power Mode
    // and in in-app browsers (Instagram, TikTok), and paints its own play
    // button over the paused video. The still underneath covers that case.
    if (frame.classList.contains('kesho-media--spin-on')) {
      const sync = () => frame.classList.toggle('is-spinning', !video.paused);
      video.addEventListener('playing', sync);
      video.addEventListener('pause', sync);
      sync();
      continue;
    }

    if (spinPreloader) afterLoad(() => spinPreloader.observe(frame));

    const button = frame.parentElement?.querySelector(':scope > [data-kesho-spin-toggle]');
    let wanted = false;

    const showSpin = () => {
      if (!wanted) return;
      frame.classList.add('is-spinning');
      button?.removeAttribute('data-loading');
    };
    video.addEventListener('playing', showSpin);

    const start = () => {
      wanted = true;
      button?.setAttribute('aria-pressed', 'true');
      if (video.readyState < 3) button?.setAttribute('data-loading', '');
      // Autoplay can still be refused; the still underneath is the fallback.
      video.play?.().then(showSpin).catch(() => stop());
    };
    const stop = () => {
      wanted = false;
      frame.classList.remove('is-spinning');
      button?.setAttribute('aria-pressed', 'false');
      button?.removeAttribute('data-loading');
      video.pause?.();
      video.currentTime = 0;
    };

    if (!canHover) {
      button?.addEventListener('click', () => (wanted ? stop() : start()));
      continue;
    }

    frame.addEventListener('pointerenter', start);
    frame.addEventListener('pointerleave', stop);
    frame.addEventListener('focusin', start);
    frame.addEventListener('focusout', stop);
  }
}

/* ------------------------------------------------------- photo fade-in -- */

/**
 * Product photos are rendered with `.kesho-img-loading`: hidden, over a soft
 * placeholder, so a slow photo reads as "on its way" rather than a blank frame.
 * Each is revealed the moment it has loaded, or failed. A photo that finished
 * before this script ran is revealed straight away, and a CSS failsafe shows
 * anything still hidden after 6s, so no photo can be stranded.
 */
function initImageFades(root = document) {
  for (const img of root.querySelectorAll('img.kesho-img-loading')) {
    const reveal = () => img.classList.remove('kesho-img-loading');
    if (img.complete && img.naturalWidth > 0) {
      reveal();
      continue;
    }
    img.addEventListener('load', reveal, { once: true });
    img.addEventListener('error', reveal, { once: true });
  }
}

/**
 * Gallery photos other than the first are lazy and sit in hidden frames, so
 * each used to start downloading only when its thumbnail was tapped — a wait on
 * every tap. Once the page has loaded (so after the opening photo), the rest are
 * fetched in the background at low priority, so switching is instant. Skipped
 * under Data Saver.
 */
function initGalleryPreload(root = document) {
  if (navigator.connection?.saveData === true) return;

  for (const gallery of root.querySelectorAll('[data-kesho-gallery]')) {
    const images = [...gallery.querySelectorAll('.kesho-gallery__frame img')];
    if (images.length < 2) continue;

    const fetchRest = () => {
      for (const img of images.slice(1)) {
        img.fetchPriority = 'low';
        img.loading = 'eager';
      }
    };
    afterLoad(fetchRest);
  }
}

/* ---------------------------------------------------------------- init -- */

scanReveals();
initVariantPicker();
initArchiveFilters();
initProductGallery();
initSpinPreviews();
initImageFades();
initGalleryPreload();
setTimeout(revealAll, 2600);
// Scroll does not bubble, but the capture phase still reaches the document, so
// one listener covers the window and `.page-wrapper` alike.
document.addEventListener('scroll', (event) => onScroll(event.target), { passive: true, capture: true });
onScroll(document.querySelector('.page-wrapper'));

// The theme editor swaps section markup in and out without a page load
document.addEventListener('shopify:section:load', (event) => {
  scanReveals();
  initVariantPicker(event.target);
  initArchiveFilters(event.target);
  initProductGallery(event.target);
  initSpinPreviews(event.target);
  initImageFades(event.target);
  initGalleryPreload(event.target);
});
